/* eslint-disable react-refresh/only-export-components -- route config module, not a Fast Refresh boundary */
import { createBrowserRouter, Navigate } from "react-router";
import { lazy, Suspense, type ReactNode } from "react";
import { AuthenticatedRouteGuard } from "./AuthenticatedRouteGuard";
import { Pending } from "@/pages/Pending";
import { Home } from "@/pages/Home";
import { DefaultPageLayout } from "@/layouts/DefaultPageLayout";
import { Recipes } from "@/pages/Recipes";
import { Recipe2 } from "@/pages/Recipe2";
import { App } from "./App";
import { StandardPageLayout } from "@/layouts/StandardPageLayout";
import { RecipeTypeRouteGuard } from "./RecipeTypeRouteGuard";
import { BrandLoader } from "@/components/BrandLoader";

// Lazily loaded routes — kept out of the initial entry chunk since none are
// needed on a typical home/browse visit: the recipe form (react-hook-form + the
// heavy import flows), the manage/library page (the HeroUI data table), login,
// the rarely-hit legal pages, and the dev-only design system. Home, the central
// Recipes browse grid, and the recipe detail page stay eager as core UX.
const NewRecipe = lazy(() => import("@/pages/NewRecipe").then((m) => ({ default: m.NewRecipe })));
const EditRecipe = lazy(() => import("@/pages/EditRecipe").then((m) => ({ default: m.EditRecipe })));
const Manage = lazy(() => import("@/pages/Manage").then((m) => ({ default: m.Manage })));
const Login = lazy(() => import("@/pages/Login").then((m) => ({ default: m.Login })));
const DesignSystem = lazy(() => import("@/pages/DesignSystem").then((m) => ({ default: m.DesignSystem })));
const Privacy = lazy(() => import("@/pages/Privacy").then((m) => ({ default: m.Privacy })));
const Terms = lazy(() => import("@/pages/Terms").then((m) => ({ default: m.Terms })));

// In-shell routes render under the already-mounted site header, so the inline
// loader fills the content area. Public legal pages have no shell yet, so they
// use the full-screen loader while their chunk resolves.
const lazyRoute = (element: ReactNode): ReactNode => <Suspense fallback={<BrandLoader />}>{element}</Suspense>;
const lazyPublicRoute = (element: ReactNode): ReactNode => (
    <Suspense fallback={<BrandLoader fullScreen />}>{element}</Suspense>
);

export const router = createBrowserRouter([
    {
        path: "/",
        element: <App />,
        children: [
            // Public routes
            { path: "login", element: lazyPublicRoute(<Login />) },
            // Signed in but not on the allowlist — sibling of /login, outside
            // the AuthenticatedRouteGuard subtree.
            { path: "pending", element: <Pending /> },
            // Public legal pages — must be reachable without auth (Google's OAuth
            // verification crawls these URLs).
            { path: "privacy", element: lazyPublicRoute(<Privacy />) },
            { path: "terms", element: lazyPublicRoute(<Terms />) },
            // Protected routes
            {
                path: "",
                element: (
                    <AuthenticatedRouteGuard>
                        <DefaultPageLayout />
                    </AuthenticatedRouteGuard>
                ),
                children: [
                    {
                        path: "",
                        element: <Home />
                    },
                    {
                        path: "recipes",
                        element: <Recipes />
                    },
                    {
                        path: "recipes/type/:typePath",
                        element: <RecipeTypeRouteGuard />
                    },
                    {
                        // No StandardPageLayout here — NewRecipe manages its own
                        // layout per state (landing = full-bleed, form = contained)
                        path: "recipes/new",
                        element: lazyRoute(<NewRecipe />)
                    },
                    {
                        path: "recipes/edit/:recipeId",
                        element: lazyRoute(
                            <StandardPageLayout>
                                <EditRecipe />
                            </StandardPageLayout>
                        )
                    },
                    { path: "recipes/:recipeId", element: <Recipe2 /> },
                    { path: "manage", element: lazyRoute(<Manage />) },
                    { path: "design-system", element: lazyRoute(<DesignSystem />) },
                    { path: "*", element: <Navigate to="/" replace /> }
                ]
            }
        ]
    }
]);
